import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import { Section, Badge, Empty, Button, Input, Select } from "@/components/ui/kit";
import { fmtINR, fmtDate } from "@/utils/format";
import { Trash2, Edit2, Save, X, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function DonorDetail() {
  const { donorId } = useParams();
  const nav = useNavigate();
  
  const [d, setD] = useState(null);
  const [summary, setSummary] = useState(null);
  const [err, setErr] = useState(null);
  
  const [isEditing, setIsEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({});
  const [nats, setNats] = useState([]);

  useEffect(() => {
    loadData();
    api.get(`/donors/${donorId}`).then(r => {
      const data = r.data.data;
      setD(data);
      initForm(data);
      if (data.donorType === "INDIVIDUAL") {
        api.get("/master/nationalities").then(res => setNats(res.data.data)).catch(()=>{});
      }
    }).catch(e => setErr(e.response?.data?.message || "Failed to load"));
    
    api.get(`/donations/donor/${donorId}/summary`).then(r => setSummary(r.data.data)).catch(()=>{});
  }, [donorId]);

  const loadData = () => {
    api.get(`/donors/${donorId}`).then(r => {
      setD(r.data.data);
      initForm(r.data.data);
    }).catch(e => setErr(e.response?.data?.message));
  };

  const initForm = (data) => {
    const isInd = data.donorType === "INDIVIDUAL";
    let dob = data.individual?.dateOfBirth;
    if (dob && dob.includes("T")) dob = dob.split("T")[0];

    let sDate = data.individual?.specialDate;
    if (sDate && sDate.includes("T")) sDate = sDate.split("T")[0];

    setForm({
      ...data,
      ...(isInd ? data.individual : data.corporate),
      dateOfBirth: dob || "",
      specialDate: sDate || "",
      loginPassword: "" // Kept blank to avoid sending the hash back
    });
  };

  const upd = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleDelete = async () => {
    const name = d.donorType === "INDIVIDUAL" ? d.individual?.fullName : d.corporate?.companyName;
    if (window.confirm(`Are you sure you want to delete ${name}? This action cannot be undone.`)) {
      try {
        await api.delete(`/donors/${donorId}`);
        toast.success("Donor deleted successfully");
        nav("/admin/donors");
      } catch (error) {
        toast.error("Failed to delete donor");
      }
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const isInd = d.donorType === "INDIVIDUAL";
      const payload = { ...form };
      
      if (isInd) {
        payload.nationalityId = form.nationalityId ? Number(form.nationalityId) : null;
        if (!payload.dateOfBirth) payload.dateOfBirth = null;
        if (!payload.specialDate) payload.specialDate = null;
      }
      
      await api.put(`/donors/${isInd ? "individual" : "corporate"}/${donorId}`, payload);
      toast.success("Donor updated successfully");
      setIsEditing(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update donor");
    } finally {
      setBusy(false);
    }
  };

  if (err) return <Empty title="Donor not found" note={err} />;
  if (!d) return <div className="text-muted-foreground p-8">Loading…</div>;
  
  const isInd = d.donorType === "INDIVIDUAL";
  const displayName = isInd 
    ? `${d.individual?.salutation ? d.individual.salutation + " " : ""}${d.individual?.fullName || ""}` 
    : d.corporate?.companyName;

  return (
    <div className="space-y-6" data-testid="donor-detail">
      <Section 
        title={displayName} 
        kicker={<span className="mono">{d.donorCode}</span>}
        right={
          <div className="flex items-center gap-3">
            <Badge tone={isInd ? "brand" : "indigo"}>{d.donorType}</Badge>
            <div className="w-px h-6 bg-emerald-900/10 mx-1"></div>
            
            {!isEditing ? (
              <>
                <Button variant="secondary" onClick={() => setIsEditing(true)}>
                  <Edit2 className="w-4 h-4 mr-2" /> Edit
                </Button>
                <button onClick={handleDelete} className="text-red-600 hover:text-red-800 transition-colors p-2" title="Delete Donor">
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            ) : (
              <Button variant="secondary" onClick={() => { setIsEditing(false); initForm(d); }}>
                <X className="w-4 h-4 mr-2" /> Cancel
              </Button>
            )}
          </div>
        } 
      />

      {isEditing ? (
        // ==========================================
        // EDIT MODE (Inputs matching DonorAdd layout)
        // ==========================================
        <form onSubmit={handleUpdate} className="space-y-8 bg-white p-6 rounded-xl border border-emerald-900/10 shadow-sm">
          {isInd ? (
            <>
              <Panel title="Personal Information">
                <div className="flex gap-2 items-end sm:col-span-2">
                  <div className="w-28 shrink-0">
                    <Select label="Title" value={form.salutation || ""} onChange={upd("salutation")}>
                      <option value="Mr.">Mr.</option>
                      <option value="Ms.">Ms.</option>
                      <option value="Mrs.">Mrs.</option>
                      <option value="Dr.">Dr.</option>
                      <option value="Prof.">Prof.</option>
                      <option value="">None</option>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Input label="Full name" required value={form.fullName || ""} onChange={upd("fullName")} />
                  </div>
                </div>

                <Select label="Gender" value={form.gender || "MALE"} onChange={upd("gender")}>
                  <option value="MALE">Male</option>
                  <option value="FEMALE">Female</option>
                  <option value="OTHER">Other</option>
                </Select>
                <Input label="Date of birth" type="date" value={form.dateOfBirth || ""} onChange={upd("dateOfBirth")} />
                
                <Select label="Nationality" value={form.nationalityId || ""} onChange={upd("nationalityId")} required>
                  <option value="">— select —</option>
                  {nats.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
                </Select>

                <Input label="Special Date Title" placeholder="e.g. Anniversary" value={form.specialDateTitle || ""} onChange={upd("specialDateTitle")} />
                <Input label="Special Date" type="date" value={form.specialDate || ""} onChange={upd("specialDate")} />
              </Panel>
              
              <Panel title="Contact Details">
                <Input label="Email Address" type="email" required value={form.email || ""} onChange={upd("email")} />
                <Input label="Primary Contact No" value={form.primaryContactNo || ""} onChange={upd("primaryContactNo")} />
                <Input label="WhatsApp No" value={form.whatsappNo || ""} onChange={upd("whatsappNo")} />
                <Input label="PAN Number" value={form.panNumber || ""} onChange={upd("panNumber")} />
              </Panel>
              
              <Panel title="Address">
                <div className="sm:col-span-2 lg:col-span-4">
                  <Input label="Address line 1" value={form.addressLine1 || ""} onChange={upd("addressLine1")} />
                </div>
                <div className="sm:col-span-2 lg:col-span-4">
                  <Input label="Address line 2" value={form.addressLine2 || ""} onChange={upd("addressLine2")} />
                </div>
                <Input label="City" value={form.city || ""} onChange={upd("city")} />
                <Input label="District" value={form.district || ""} onChange={upd("district")} />
                <Input label="State" value={form.state || ""} onChange={upd("state")} />
                <Input label="Country" value={form.country || "India"} onChange={upd("country")} />
                <Input label="Pincode" value={form.pincode || ""} onChange={upd("pincode")} />
              </Panel>
              
              <Panel title="Professional & Login">
                <Input label="Current Employer" value={form.currentEmployer || ""} onChange={upd("currentEmployer")} />
                <Input label="Referred by" value={form.referredBy || ""} onChange={upd("referredBy")} />
                <Input label="Portal Login Password" type="text" placeholder="Leave blank to keep unchanged" value={form.loginPassword || ""} onChange={upd("loginPassword")} />
              </Panel>
            </>
          ) : (
            <>
              <Panel title="Company Details">
                <div className="sm:col-span-2">
                  <Input label="Company Name" required value={form.companyName || ""} onChange={upd("companyName")} />
                </div>
                <Input label="Company PAN Number" value={form.panNumber || ""} onChange={upd("panNumber")} />
              </Panel>
              
              <Panel title="Primary Contact">
                <Input label="Full Name" required value={form.primaryContactName || ""} onChange={upd("primaryContactName")} />
                <Input label="Email Address" type="email" required value={form.primaryContactEmail || ""} onChange={upd("primaryContactEmail")} />
                <Input label="Phone Number" value={form.primaryContactPhone || ""} onChange={upd("primaryContactPhone")} />
                <Input label="WhatsApp Number" value={form.primaryContactWhatsapp || ""} onChange={upd("primaryContactWhatsapp")} />
              </Panel>

              <Panel title="Secondary Contact (Optional)">
                <Input label="Full Name" value={form.secondaryContactName || ""} onChange={upd("secondaryContactName")} />
                <Input label="Email Address" type="email" value={form.secondaryContactEmail || ""} onChange={upd("secondaryContactEmail")} />
                <Input label="Phone Number" value={form.secondaryContactPhone || ""} onChange={upd("secondaryContactPhone")} />
                <Input label="WhatsApp Number" value={form.secondaryContactWhatsapp || ""} onChange={upd("secondaryContactWhatsapp")} />
              </Panel>

              <Panel title="Location & Login">
                <div className="sm:col-span-2 lg:col-span-4">
                  <Input label="Registered Office Address" value={form.registeredOfficeAddress || ""} onChange={upd("registeredOfficeAddress")} />
                </div>
                <Input label="City" value={form.city || ""} onChange={upd("city")} />
                <Input label="District" value={form.district || ""} onChange={upd("district")} />
                <Input label="State" value={form.state || ""} onChange={upd("state")} />
                <Input label="Country" value={form.country || "India"} onChange={upd("country")} />
                <Input label="Pincode" value={form.pincode || ""} onChange={upd("pincode")} />
                <Input label="Portal Login Password" type="text" placeholder="Leave blank to keep unchanged" value={form.loginPassword || ""} onChange={upd("loginPassword")} />
              </Panel>
            </>
          )}
          
          <div className="pt-4 border-t border-emerald-900/10 flex justify-end">
            <Button type="submit" disabled={busy}>
              <Save className="w-4 h-4 mr-2" /> Save Changes
            </Button>
          </div>
        </form>
      ) : (
        // ==========================================
        // VIEW MODE (Read-only panels mirroring form structure)
        // ==========================================
        <div className="space-y-8">
          {isInd ? (
            <>
              <Panel title="Personal Information">
                <div className="sm:col-span-2 flex gap-4">
                  <ReadOnlyField label="Title" value={d.individual?.salutation} />
                  <ReadOnlyField label="Full Name" value={d.individual?.fullName} />
                </div>
                <ReadOnlyField label="Gender" value={d.individual?.gender} />
                <ReadOnlyField label="Date of birth" value={fmtDate(d.individual?.dateOfBirth)} />
                <ReadOnlyField label="Nationality" value={d.individual?.nationalityName} />
                <ReadOnlyField label="Special Date Title" value={d.individual?.specialDateTitle} />
                <ReadOnlyField label="Special Date" value={fmtDate(d.individual?.specialDate)} />
              </Panel>

              <Panel title="Contact Details">
                <div className="sm:col-span-2">
                  <ReadOnlyField label="Email Address" value={d.individual?.email} />
                </div>
                <ReadOnlyField label="Primary Contact No" value={d.primaryContactNo} />
                <ReadOnlyField label="WhatsApp No" value={d.whatsappNo} />
                <ReadOnlyField label="PAN Number" value={d.panNumber} mono />
              </Panel>

              <Panel title="Address">
                <div className="sm:col-span-2 lg:col-span-4">
                  <ReadOnlyField label="Address Line 1" value={d.individual?.addressLine1} />
                </div>
                <div className="sm:col-span-2 lg:col-span-4">
                  <ReadOnlyField label="Address Line 2" value={d.individual?.addressLine2} />
                </div>
                <ReadOnlyField label="City" value={d.city} />
                <ReadOnlyField label="District" value={d.district} />
                <ReadOnlyField label="State" value={d.state} />
                <ReadOnlyField label="Country" value={d.country} />
                <ReadOnlyField label="Pincode" value={d.pincode} />
              </Panel>

              <Panel title="Professional & Login">
                <ReadOnlyField label="Current Employer" value={d.individual?.currentEmployer} />
                <ReadOnlyField label="Referred by" value={d.individual?.referredBy} />
                <ReadOnlyField 
                  label="Portal Login Password" 
                  value={<span className="flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5"/>•••••••• (Encrypted)</span>} 
                />
              </Panel>
            </>
          ) : (
            <>
              <Panel title="Company Details">
                <div className="sm:col-span-2">
                  <ReadOnlyField label="Company Name" value={d.corporate?.companyName} />
                </div>
                <ReadOnlyField label="Company PAN Number" value={d.panNumber} mono />
              </Panel>

              <Panel title="Primary Contact">
                <ReadOnlyField label="Full Name" value={d.corporate?.primaryContactName} />
                <div className="sm:col-span-2">
                  <ReadOnlyField label="Email Address" value={d.corporate?.primaryContactEmail} />
                </div>
                <ReadOnlyField label="Phone Number" value={d.corporate?.primaryContactPhone} />
                <ReadOnlyField label="WhatsApp Number" value={d.corporate?.primaryContactWhatsapp} />
              </Panel>

              <Panel title="Secondary Contact (Optional)">
                <ReadOnlyField label="Full Name" value={d.corporate?.secondaryContactName} />
                <div className="sm:col-span-2">
                  <ReadOnlyField label="Email Address" value={d.corporate?.secondaryContactEmail} />
                </div>
                <ReadOnlyField label="Phone Number" value={d.corporate?.secondaryContactPhone} />
                <ReadOnlyField label="WhatsApp Number" value={d.corporate?.secondaryContactWhatsapp} />
              </Panel>

              <Panel title="Location & Login">
                <div className="sm:col-span-2 lg:col-span-4">
                  <ReadOnlyField label="Registered Office Address" value={d.corporate?.registeredOfficeAddress} />
                </div>
                <ReadOnlyField label="City" value={d.city} />
                <ReadOnlyField label="District" value={d.district} />
                <ReadOnlyField label="State" value={d.state} />
                <ReadOnlyField label="Country" value={d.country} />
                <ReadOnlyField label="Pincode" value={d.pincode} />
                <ReadOnlyField 
                  label="Portal Login Password" 
                  value={<span className="flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5"/>•••••••• (Encrypted)</span>} 
                />
              </Panel>
            </>
          )}
        </div>
      )}

      {!isEditing && summary && (
        <div className="rounded-xl border border-emerald-900/10 bg-white p-6 shadow-sm mt-6" data-testid="donor-summary">
          <div className="overline text-emerald-800">Contribution summary</div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Lifetime" value={fmtINR(summary.lifetime)} />
            <Stat label="Current AY" value={fmtINR(summary.currentAcademicYear)} />
            <Stat label="Matched" value={fmtINR(summary.matched)} />
            <Stat label="Donations" value={summary.count} />
          </div>
          <div className="mt-6">
            <div className="overline mb-2 text-emerald-800">By project</div>
            {summary.byProject?.length ? summary.byProject.map((p,i) => (
              <div key={i} className="flex justify-between py-2 border-b border-emerald-900/5 text-sm">
                <span className="text-emerald-950 font-medium">{p.name}</span>
                <span className="mono font-semibold text-emerald-900">{fmtINR(p.amount)}</span>
              </div>
            )) : <div className="text-xs text-muted-foreground">No contributions yet.</div>}
          </div>
        </div>
      )}
      
      <div className="pt-4">
        <Link to="/admin/donors" data-testid="back-to-donors" className="text-emerald-800 text-sm font-semibold hover:underline">← Back to donors</Link>
      </div>
    </div>
  );
}

// Reusable Panel Wrapper matching DonorAdd format
function Panel({ title, children }){
  return (
    <div className="rounded-xl border border-emerald-900/10 bg-white p-6 shadow-sm">
      <div className="overline mb-4 text-emerald-800 font-semibold uppercase tracking-wider text-sm">{title}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {children}
      </div>
    </div>
  );
}

// Reusable Non-Overlapping Read-Only Field
function ReadOnlyField({ label, value, mono }){
  return (
    <div className="space-y-1 min-w-0">
      <div className="text-[12px] font-medium text-emerald-800/80 uppercase tracking-wide truncate">
        {label}
      </div>
      <div className={`text-[15px] break-words ${mono ? "font-mono text-emerald-900" : "text-emerald-950 font-medium"}`}>
        {value || "—"}
      </div>
    </div>
  );
}

// Contribution Stats Component
function Stat({ label, value }){ 
  return (
    <div>
      <div className="overline text-muted-foreground">{label}</div>
      <div className="font-display text-2xl font-bold text-emerald-900">{value}</div>
    </div>
  ); 
}